import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Grid,
  Snackbar,
} from "@mui/material";
import { AppConfig, roles, LEGAL_CHOOSE_FILE_SUBQUESTION_ID } from "@configs/admin-config";
import { checkIfRoleExists } from "@utils/oauth";
import MuiAlert from "@mui/material/Alert";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  questionParent: {
    display: "flex",
  },
  questionIndex: {
    flexBasis: "4%",
    "@media(max-width: 900px)": {
      flexBasis: "5%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "10%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "15%",
    },
  },
  questionDetails: {
    flexBasis: "96%",
    textAlign: "justify",
    "@media(max-width: 900px)": {
      flexBasis: "94%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "90%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "85%",
    },
  },
  verticalcenter: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  emailMsg: {
    color: "grey",
  },
  linearProgress: {
    marginTop: 15,
    marginBottom: 5,
    width: "100%",
  },
  fileBlock: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: "8px 12px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "95%",
    "&:hover": {
      backgroundColor: "#ebebeb",
    },
  },
  fileIcon: {
    marginRight: 8,
    fontSize: 20,
    color: "#666",
  },
  fileName: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    color: "#333",
    marginRight: 4,
  },
  attachedFilesSection: {
    marginTop: 8,
    marginBottom: 8,
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const SubQuestionDetails = (props) => {
  const { classes } = useStyles();
  const [question, setQuestion] = useState(props.question);
  const [subQuestion, setSubQuestion] = useState(props.subQuestion);
  const [boolAnswer, setBoolAnswer] = useState(false);
  const [uploadedFiles, setFiles] = useState([]);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });

  const { disableCall, fieldDisabled, answer, answers, files } = props;
  
  useEffect(() => {
    disableCall(fieldDisabled, answer);

    // Converting Mysql 0/1 into Boolean True/False
    if (answers && answers[0] && answers[0].booleanAnswer) {
      setBoolAnswer(true);
    } else if (answers && answers[0] && !answers[0].booleanAnswer) {
      setBoolAnswer(false);
    }

    if (files && files.length > 0) {
      const relevantFiles = files.filter(
        (file) => file.subQuestionId === subQuestion.subQuestionId
      );
      setFiles(relevantFiles); 
    } else {
      setFiles([]);
    }
  }, [subQuestion.subQuestionId, subQuestion.answerType, files, disableCall, fieldDisabled, answer, answers]);

  useEffect(() => {
    setQuestion(props.question);
    setSubQuestion(props.subQuestion);
  }, [props.question, props.subQuestion]);

  useEffect(() => {
    if (!props.callbackAnswer && props.answer && props.answer.length) {
      if (props.answer[0]?.booleanAnswer !== undefined && props?.subQuestion?.answerType) {
        setBoolAnswer(Boolean(props.answer[0].booleanAnswer));
      }
    } else if (props.callbackAnswer) {
      if (props.callbackAnswer[0]?.booleanAnswer !== undefined && props?.subQuestion?.answerType) {
        setBoolAnswer(Boolean(props.callbackAnswer[0].booleanAnswer));
      }
    }
  }, [props.answer, props.callbackAnswer, props?.subQuestion?.answerType]);

  // Handle Radio Button Inputs
  const handleRadioChange = (subQuestionId) => (event) => {
    const value = event.target.value;
    // If yes is selected
    if (value === "yes") {
      setBoolAnswer(true);
      props.parentCallback(subQuestionId, true, "", "", true);
    }
    // If no is selected
    else {
      setBoolAnswer(false);
      props.parentCallback(subQuestionId, false, "", "", true);
    }
  };

  // Handle Text Inputs
  const handleFormChange = (subQuestionId) => (event) => {
    const newValue = event.target.value;
    const currentValue = props.answer && props.answer[0] ? props.answer[0].descriptionAnswer : "";

    if (newValue !== currentValue) {
      props.parentCallback(subQuestionId, "", newValue, "", true);
    }
  };

  const buildFileViewUrl = (baseUrl, fileName, extensionWithoutDot) => {
    return `${AppConfig.urls.ADMIN}${baseUrl}?fileName=${encodeURIComponent(
      fileName
    )}&extension=${encodeURIComponent(extensionWithoutDot)}`;
  };

  const handleChipOnClick = (fileName) => {
    var extension = fileName.split('.').pop();
    var extensionWithoutDot = extension.toLowerCase();

    const PDF_URL = buildFileViewUrl(AppConfig.urls.VIEW_PDF, fileName, extensionWithoutDot);
    const IMAGE_URL = buildFileViewUrl(AppConfig.urls.VIEW_IMAGE, fileName, extensionWithoutDot);

    if (extension === "pdf") {
      window.open(PDF_URL, "_blank");
    } else if (extension === "jpg") {
      window.open(IMAGE_URL, "_blank");
    } else if (extension === "jpeg") {
      window.open(IMAGE_URL, "_blank");
    } else if (extension === "png") {
      window.open(IMAGE_URL, "_blank");
    } else {
      const url = `${AppConfig.FileServiceURL}${fileName}?fileExtension=${extensionWithoutDot}`;
      window.open(url, "_blank");
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarData({ ...snackbarData, open: false });
  };

  return (
    <React.Fragment>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={snackbarData.open} onClose={handleClose} autoHideDuration={6000}>
        <Alert onClose={handleClose} severity={snackbarData.severity}>
          {snackbarData.message}
        </Alert>
      </Snackbar>
      {/* Render Boolean Input */}
      {question.questionId === subQuestion.questionId && subQuestion.answerType && (
        <Grid item xs={12}>
          <div className={classes.questionParent}>
            <div className={classes.questionIndex}></div>
            <div className={classes.questionDetails}>
              <FormControl
                component="fieldset"
                disabled={checkIfRoleExists(roles.legalApprover) ? props.disabled || props.fieldDisabled : true}
              >
                <RadioGroup
                  row
                  id={subQuestion.subQuestionId}
                  onChange={handleRadioChange(subQuestion.subQuestionId)}
                  defaultValue={props.answer && props.answer[0] ? (props.answer[0].booleanAnswer ? "yes" : "no") : ""}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio color="primary" />}
                    label={props.categoryId === "3" ? "Approve" : "Yes"}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio color="primary" />}
                    label={props.categoryId === "3" ? "Reject" : "No"}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
        </Grid>
      )}

      {/* Render Description Input for Boolean Input Yes */}
      {question.questionId === subQuestion.questionId &&
        subQuestion.answerDescription &&
        subQuestion.answerType &&
        boolAnswer && (
          <Grid item xs={12}>
            <div className={classes.questionParent}>
              <div className={classes.questionIndex}></div>
              <div className={classes.questionDetails}>
                <TextField
                  name={subQuestion.subQuestionId + ""}
                  defaultValue={props.answer && props.answer[0] ? props.answer[0].descriptionAnswer : ""}
                  size="small"
                  minRows={3}
                  multiline
                  onChange={handleFormChange(subQuestion.subQuestionId)}
                  label={subQuestion.description}
                  autoComplete="nope"
                  error={props.isError}
                  helperText={props.isError ? "Required" : null}
                  variant="outlined"
                  disabled={checkIfRoleExists(roles.legalApprover) ? props.disabled || props.fieldDisabled : true}
                  fullWidth
                />
              </div>
            </div>
          </Grid>
        )}

      {/* Render Description Input Only */}
      {question.questionId === subQuestion.questionId && subQuestion.answerDescription && !subQuestion.answerType && (
        <Grid item xs={12}>
          <div className={classes.questionParent}>
            <div className={classes.questionIndex}></div>
            <div className={classes.questionDetails}>
              <TextField
                name={subQuestion.subQuestionId + ""}
                defaultValue={props.answer && props.answer[0] ? props.answer[0].descriptionAnswer : ""}
                size="small"
                multiline={props.categoryId === "3"}
                minRows={props.categoryId === "3" ? 3 : 1}
                onChange={handleFormChange(subQuestion.subQuestionId)}
                label={subQuestion.description}
                autoComplete="nope"
                variant="outlined"
                disabled={checkIfRoleExists(roles.legalApprover) ? props.disabled || props.fieldDisabled : true}
                fullWidth
              />
            </div>
          </div>
        </Grid>
      )}

      {/* Render File upload Input */}
      {question.questionId === subQuestion.questionId &&
        subQuestion.answerFileUpload &&
        subQuestion.subQuestionId !== LEGAL_CHOOSE_FILE_SUBQUESTION_ID && (
        <Grid item xs={12}>
          <div className={classes.questionParent}>
            <div className={classes.questionIndex}></div>
            <div className={classes.questionDetails}>
              <Grid container spacing={2}>
                {subQuestion.description !== "" && (
                  <Grid item xs={12}>
                    <Typography style={{ paddingTop: '5px', marginBottom: '8px' }}>
                      {subQuestion.description}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <div className={classes.attachedFilesSection}>
                    {uploadedFiles.map((file, key) => {
                      return (
                        <div 
                          key={key} 
                          className={classes.fileBlock}
                          onClick={() => handleChipOnClick(file.fileName)}
                        >
                          <InsertDriveFileIcon className={classes.fileIcon} />
                          <span className={classes.fileName}>
                            {file.fileName.substring(24, file.fileName.length).substring(0, 18)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Grid>
              </Grid>
            </div>
          </div>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default SubQuestionDetails;
